import { useRef, useState, type ChangeEvent } from 'react';
import { TRAINING_CORRECTIONS_FILE_PARSE_ERROR } from '../../../features/chatbots/chatbotTrainingCorrectionsFile';
import type { useChatbotTrainingTab } from '../../../features/chatbots/hooks/useChatbotTrainingTab';
import {
  useCreateChatbotTrainingExampleMutation,
  useDeleteChatbotTrainingExampleMutation,
  useExportChatbotTrainingExamplesMutation,
  useImportChatbotTrainingExamplesMutation,
  useUpdateChatbotTrainingExampleMutation,
} from '../../../features/chatbots/mutations/useChatbotTrainingExampleMutations';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type { ChatbotTrainingExampleWithIdentity } from '../../../types/chatbot';
import { CreateTrainingExampleDialog } from '../CreateTrainingExampleDialog';
import { ChatbotTrainingExampleList } from './ChatbotTrainingExampleList';
import { ChatbotTrainingToolbar } from './ChatbotTrainingToolbar';
import './ChatbotTrainingTab.css';

type ChatbotTrainingTabProps = {
  chatbotId: string;
  chatbotName: string;
  trainingTab: ReturnType<typeof useChatbotTrainingTab>;
  canManageTraining: boolean;
};

export function ChatbotTrainingTab({
  chatbotId,
  chatbotName,
  trainingTab,
  canManageTraining,
}: ChatbotTrainingTabProps) {
  const { translate } = useI18n();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const createMutation = useCreateChatbotTrainingExampleMutation(chatbotId);
  const updateMutation = useUpdateChatbotTrainingExampleMutation(chatbotId);
  const deleteMutation = useDeleteChatbotTrainingExampleMutation(chatbotId);
  const exportMutation = useExportChatbotTrainingExamplesMutation(
    chatbotId,
    chatbotName,
  );
  const importMutation = useImportChatbotTrainingExamplesMutation(chatbotId);
  const {
    examples,
    trainingExamplesQuery,
    isPageLoading,
    searchInput,
    hasActiveTrainingSearch,
    handleSearchChange,
    handleManualRefresh,
  } = trainingTab;

  const handleCreate = ({
    question,
    answer,
    sortOrder,
  }: {
    question: string;
    answer: string;
    sortOrder: number | null;
  }) => {
    createMutation.mutate(
      { question, answer, sortOrder },
      {
        onSuccess: () => {
          toast.success({
            title: translate('chatbots.training.createSuccessTitle'),
            description: '',
          });
          setIsCreateDialogOpen(false);
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.training.createErrorTitle'),
          );
        },
      },
    );
  };

  const handleUpdate = (
    example: ChatbotTrainingExampleWithIdentity,
    input: { question: string; answer: string; sortOrder: number | null },
    options: { onSuccess: () => void },
  ) => {
    updateMutation.mutate(
      {
        example,
        question: input.question,
        answer: input.answer,
        sortOrder: input.sortOrder,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('chatbots.training.updateSuccessTitle'),
            description: '',
          });
          options.onSuccess();
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.training.updateErrorTitle'),
          );
        },
      },
    );
  };

  const handleDelete = (example: ChatbotTrainingExampleWithIdentity) => {
    deleteMutation.mutate(example.id, {
      onSuccess: () => {
        toast.success({
          title: translate('chatbots.training.deleteSuccessTitle'),
          description: '',
        });
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('chatbots.training.deleteErrorTitle'),
        );
      },
    });
  };

  const handleExport = () => {
    if (exportMutation.isPending || importMutation.isPending) {
      return;
    }
    exportMutation.mutate(undefined, {
      onError: (error) => {
        void toastMutationError(
          error,
          translate('chatbots.training.exportErrorTitle'),
        );
      },
    });
  };

  const handleImportFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || importMutation.isPending || exportMutation.isPending) {
      return;
    }

    importMutation.mutate(file, {
      onSuccess: (data) => {
        toast.success({
          title: translate('chatbots.training.importSuccessTitle', {
            count: String(data ?? 0),
          }),
          description: '',
        });
      },
      onError: (error) => {
        const isParseError =
          error instanceof Error &&
          error.message === TRAINING_CORRECTIONS_FILE_PARSE_ERROR;
        void toastMutationError(
          error,
          isParseError
            ? translate('chatbots.training.importInvalidFileTitle')
            : translate('chatbots.training.importErrorTitle'),
        );
      },
    });
  };

  const emptyMessage = hasActiveTrainingSearch
    ? translate('chatbots.training.noResults')
    : canManageTraining
      ? translate('chatbots.training.empty')
      : translate('chatbots.training.emptyReadOnly');
  const isImportExportBusy =
    exportMutation.isPending || importMutation.isPending;

  return (
    <section className="chatbot-training">
      {canManageTraining && (
        <input
          ref={importFileInputRef}
          type="file"
          hidden
          accept="application/json,.json"
          onChange={handleImportFileChange}
        />
      )}
      <ChatbotTrainingToolbar
        searchInput={searchInput}
        onSearchChange={handleSearchChange}
        canManageTraining={canManageTraining}
        isImportExportBusy={isImportExportBusy}
        importProgress={importMutation.progress}
        exportProgress={exportMutation.progress}
        isImportPending={importMutation.isPending}
        isExportPending={exportMutation.isPending}
        onCreateClick={() => setIsCreateDialogOpen(true)}
        onImportClick={() => importFileInputRef.current?.click()}
        onExport={handleExport}
        onManualRefresh={handleManualRefresh}
      />

      {isPageLoading ? (
        <p className="chatbot-training__status">
          {translate('chatbots.training.loading')}
        </p>
      ) : trainingExamplesQuery.isError ? (
        <p className="chatbot-training__status" role="alert">
          {translate('chatbots.training.loadError')}
        </p>
      ) : examples.length === 0 ? (
        <p className="chatbot-training__status">{emptyMessage}</p>
      ) : (
        <ChatbotTrainingExampleList
          examples={examples}
          canManageTraining={canManageTraining}
          savingExampleId={
            updateMutation.isPending
              ? updateMutation.variables?.example.id
              : undefined
          }
          deletingExampleId={
            deleteMutation.isPending ? deleteMutation.variables : undefined
          }
          onSave={handleUpdate}
          onDelete={handleDelete}
        />
      )}

      {canManageTraining && isCreateDialogOpen && (
        <CreateTrainingExampleDialog
          title={translate('chatbots.training.createDialogTitle')}
          questionLabel={translate('chatbots.training.questionLabel')}
          questionPlaceholder={translate(
            'chatbots.training.questionPlaceholder',
          )}
          answerLabel={translate('chatbots.training.answerLabel')}
          answerPlaceholder={translate('chatbots.training.answerPlaceholder')}
          sortOrderLabel={translate('chatbots.training.sortOrderLabel')}
          sortOrderPlaceholder={translate(
            'chatbots.training.sortOrderPlaceholder',
          )}
          sortOrderHint={translate('chatbots.training.sortOrderHint')}
          createLabel={translate('chatbots.training.createSubmit')}
          cancelLabel={translate('chatbots.training.cancel')}
          closeLabel={translate('chatbots.training.close')}
          isSubmitting={createMutation.isPending}
          onCreate={handleCreate}
          onClose={() => setIsCreateDialogOpen(false)}
        />
      )}
    </section>
  );
}
